import React, { useState, useEffect, useRef } from "react";
import { Box, Image, Text } from "@chakra-ui/react";
import Clock from "../../assets/clock.svg"; // Adjust the path as necessary

// Custom hook for stopwatch functionality
const useStopwatch = (initialSeconds = 0) => {
  const [seconds, setSeconds] = useState(initialSeconds);
  const [isRunning, setIsRunning] = useState(false);
  const intervalRef = useRef(null);

  // Start the stopwatch
  const start = () => {
    setIsRunning(true);
  };

  // Stop the stopwatch
  const stop = () => {
    setIsRunning(false);
  };

  // Reset the stopwatch
  const reset = (newInitialSeconds = 0) => {
    setIsRunning(false);
    setSeconds(newInitialSeconds);
  };

  // Reset and start the stopwatch
  const resetAndStart = (newInitialSeconds = 0) => {
    setSeconds(newInitialSeconds);
    setIsRunning(true);
  };

  // Format time as MM:SS if < 1 hour, else HH:MM:SS
  const formatTime = (totalSeconds) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;

    if (hours > 0) {
      return `${hours.toString().padStart(2, "0")}:${minutes
        .toString()
        .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    } else {
      return `${minutes.toString().padStart(2, "0")}:${secs
        .toString()
        .padStart(2, "0")}`;
    }
  };

  // Effect to handle the timer
  useEffect(() => {
    if (isRunning) {
      intervalRef.current = setInterval(() => {
        setSeconds((prevSeconds) => prevSeconds + 1);
      }, 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    // Cleanup on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isRunning]);

  return {
    seconds,
    formattedTime: formatTime(seconds),
    isRunning,
    start,
    stop,
    reset,
    resetAndStart,
  };
};

// Timer Clock Component
const TimerClock = ({
  startTimeInSeconds = 0,
  isMobile = false,
  autoStart = true,
}) => {
  const { formattedTime, start, stop, reset, resetAndStart, isRunning } =
    useStopwatch(startTimeInSeconds);

  // Auto-start the timer when component mounts (if autoStart is true)
  useEffect(() => {
    if (autoStart) {
      start();
    }
  }, []); // Remove autoStart from dependencies to prevent re-running

  // Reset timer when startTimeInSeconds changes
  useEffect(() => {
    if (autoStart) {
      resetAndStart(startTimeInSeconds);
    } else {
      reset(startTimeInSeconds);
    }
  }, [startTimeInSeconds, autoStart]);

  return (
    <Box
      display={"flex"}
      position="absolute"
      right={{ base: "4px", md: "20px", lg: "40px" }}
      alignItems="center"
    >
      <Image boxSize={isMobile ? "16px" : "24px"} src={Clock} />
      <Text
        fontFamily={"Poppins"}
        fontWeight={600}
        fontSize={isMobile ? 12 : 16}
        paddingLeft={isMobile ? "4px" : "8px"}
        letterSpacing={0}
      >
        {formattedTime}
      </Text>
    </Box>
  );
};

export default TimerClock;
